import React from 'react';
import { Component, Position, PuzzleButton, PuzzleCheckbox } from "puzzle";
import { GridCellProps } from "@progress/kendo-react-grid";
import { GridColumn as Column } from "@progress/kendo-react-grid/dist/npm/GridColumn";
import _ from 'lodash';
import { CheckboxChangeEvent } from "@progress/kendo-react-inputs";
import { FILES_GRID_CUSTOM_PROPS, JOB_SELECTION_INFO, TREE_LIST_ITEM, VIRTUAL_SCROLL } from "./Types";
import virtualScroll from "../../../VirtualScroll";
import KendoGridAbstract from "../../../util/KendoGridAbstract/KendoGridAbstract";

export type PROPS = {
  addTargetIds: (id: number) => 0,
  fileNameInputText: string,
  getBack: Function,
  initProps: FILES_GRID_CUSTOM_PROPS,
  onSelectionChange: Function,
  pageSize: number,
  requestActionName: string,
  selectionInfo: JOB_SELECTION_INFO,
  submissionId: string,
  virtualScroll: VIRTUAL_SCROLL
};

export type STATE = {
  data: TREE_LIST_ITEM[],
  pageDataPopulated: number[],
  pageNumber: number,
  requestInProgress: boolean,
  skip: number,
  total: number
}

export class CancelSubmissionFilesGrid extends Component<PROPS, STATE> {
  public state: STATE = {
    data: [],
    pageDataPopulated: [],
    pageNumber: 0,
    requestInProgress: false,
    skip: 0,
    total: 0
  };

  static defaultProps = {
    requestActionName: 'cancelSubmissionFilesGridRequest',
    pageSize: 200
  };

  rowHeight: number = 35;
  selectedFieldName: string = 'selected';

  moduleDidMount() {
    super.moduleDidMount();
    this.dispatch(this.props.requestActionName, '', this.prepareRequestParams(0));
  }

  componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<any>, snapshot?: any): void {
    const { fileNameInputText } = this.props;
    if (!_.isEqual(prevProps.fileNameInputText, fileNameInputText)) {
      this.handleFileNameFilterChange(fileNameInputText);
    }
  }

  /**
   * Grid cell render
   * @param defaultRendering
   * @param cellProps
   */
  cellRender = (defaultRendering: React.ReactElement<HTMLTableCellElement> | null, cellProps: GridCellProps): JSX.Element => {
    const { dataItem, field } = cellProps;
    if (defaultRendering && field) {
      let children;
      let editedProps = {
        ...defaultRendering.props
      };

      switch (cellProps.field) {
        case 'selected':
          editedProps.className += ' tp-select-cell';
          children = this.selectCellRenderer(cellProps);
          break;
        default:
          children = dataItem[field];
          break;
      }

      return React.cloneElement(defaultRendering, editedProps, children);
    }
    return <></>
  };

  /**
   * Gets job files from server and calls loadJobWithFiles on success
   */
  getJobFiles = (pageIndex: number, batchName: string, targetLanguage: string) => {
    const { submissionId } = this.props;
    const wordCountParams = {
      index: pageIndex,
      pageSize: this.props.pageSize,
      batchName,
      targetLanguage
    };
    this.dispatch('wordCountGetJobFiles', Position.end, {
      submissionId,
      wordCountParams
    });
  };

  /**
   * Handles change of fileName text filter
   */
  handleFileNameFilterChange = (fileNameInputText: string) => {
    const params = {
      ...this.props.initProps.params,
      fileNameInputText
    };
    this.dispatch('filterCancelSubmissionFilesGrid', {
      buffer: 300
    }, { params })
  };

  /**
   *  Handles selection change on data items
   *  Calls selection change from parent component
   *  and also marks data item selected on grid
   */
  handleSelectionChange = (dataItem: TREE_LIST_ITEM, event: CheckboxChangeEvent) => {
    const { onSelectionChange } = this.props;
    const { targetId } = dataItem;
    const isSelected = event.value;
    onSelectionChange(targetId, isSelected);
  };

  /**
   * Returns true if file is selected, false otherwise
   * @param targetId
   */
  isFileSelected = (targetId: number) => {
    const { jobSelected, targetsSelected } = this.props.selectionInfo;
    return jobSelected || targetsSelected.includes(targetId);
  };

  /**
   * Prepares request params
   * @param pageNumber
   */
  prepareRequestParams = (pageNumber: number) => {
    const { fileNameInputText, initProps } = this.props;
    const params = {
      ...initProps.params,
      fileNameInputText,
      index: pageNumber
    };
    return { params };
  };

  /**
   * Renders custom header of the grid
   */
  renderHeader = () => {
    const { languageValue, batchName } = this.props.initProps.jobDetails;
    return (
      <div className={'tp-cancel-submission-files-list-header-breadcrumb k-hbox k-align-items-center'}>
        <PuzzleButton
          aria-label={this.translate('SRL-goBack')}
          themeColor={'primary'}
          fillMode="flat"
          iconClass="tp-icon tp-back"
          onClick={this.props.getBack.bind(this)}
        />
        {batchName}
        <span className={"k-icon k-i-arrow-chevron-right"} />
        {languageValue}
      </div>
    )
  };

  /**
   *  Resets grid data
   */
  resetGridData = () => {
    this.setState({
      data: [],
      pageNumber: 0,
      skip: 0,
      pageDataPopulated: []
    })
  };

  /**
   * Data items parser before setting data in virtual scroll
   * @param data
   */
  setGridData = (data: any) => {
    const { addTargetIds } = this.props;
    data.items = data.items.map((item: any) => {
      const selected = this.isFileSelected(item.targetId);
      if (selected) {
        addTargetIds(item.targetId);
      }
      return {
        targetId: item.targetId,
        documentName: item.fileName,
        selected
      };
    });
    this.props.virtualScroll.setGridData(data);
  };

  /**
   * Renders select cell
   */
  selectCellRenderer = (cellProps: GridCellProps) => {
    const { dataItem } = cellProps;
    const { documentName } = dataItem;
    let selectCell = (
      <PuzzleCheckbox
        label={documentName}
        value={dataItem[this.selectedFieldName]}
        onChange={this.handleSelectionChange.bind(this, dataItem)}
      />
    );
    return selectCell;
  };

  /**
   * Updates selected state of displayed files
   */
  updateFields = (data: TREE_LIST_ITEM[]) => {
    data.forEach((item) => item.selected = this.isFileSelected(item.targetId as number));
    return data
  };

  renderBody() {
    const { data, skip, total } = this.state;
    const pageSize = this.props.pageSize;
    return (
      <div className={'tp-cancel-submission-files-list'}>
        <div className={'tp-cancel-submission-files-list-header'}>
          {this.renderHeader()}
        </div>
        <KendoGridAbstract
          captionText={this.translate('SRL-cancelSubmissionGridCaptionText')}
          className={'tp-no-border'}
          cellRender={this.cellRender}
          data={this.updateFields(data.slice(skip, skip + pageSize))}
          onPageChange={this.props.virtualScroll.pageChange}
          pageSize={pageSize}
          rowHeight={this.rowHeight}
          scrollable={'virtual'}
          selectedField={this.selectedFieldName}
          skip={skip}
          total={total}
        >
          <Column
            field="selected"
            filterable={false}
            headerCell={() => <span className={'k-link'}>{this.translate('SRL-fileName')}</span>}
          />
        </KendoGridAbstract>
      </div>
    )
  }

}

export default virtualScroll(CancelSubmissionFilesGrid);
